// The upsell after a learner's first review (the "great work" page). In production (owner, 2026-10-21) it is
// "today": the paywall pop-up over "great work" (EliteFirstUpsellModalContainer), always in its Elite first format,
// or T6's exam pass in its place for a signed-in Free learner on the web whose exam is 0–30 days away. The rule is
// getFirstUpsellVersionInProduction (firstExperienceGelRules.ts). From 2026-10-09 to 2026-10-21 it was version A2
// drawn in the page ("Simplified", FirstUpsellInPageA.tsx).
//
// The in-page designs compared before (owner, 2026-10-08) stay as previews in development builds only:
// `?first_upsell=today|in_page_a|in_page_a2|in_page_a3|in_page_b|in_page_c` on any page, remembered for the tab and
// read once as the app starts (App.tsx), because the app drops the query on its first navigation. A production build
// never reads it, so it never shows a, a2, a3, b or c.

import { getFirstUpsellVersionInProduction } from "../../../first-experience-tests/firstExperienceGelRules";

export type FirstUpsellVersion = "today" | "in_page_a" | "in_page_a2" | "in_page_a3" | "in_page_b" | "in_page_c";

const FIRST_UPSELL_URL_PARAMETER = "first_upsell";
const FIRST_UPSELL_VERSIONS: FirstUpsellVersion[] = [
  "today",
  "in_page_a",
  "in_page_a2",
  "in_page_a3",
  "in_page_b",
  "in_page_c",
];
const FIRST_UPSELL_STORAGE_KEY = "jungle:first-upsell";

function isFirstUpsellVersion(value: string | null): value is FirstUpsellVersion {
  return FIRST_UPSELL_VERSIONS.includes(value as FirstUpsellVersion);
}

// undefined: not read yet; null: no preview chosen
let firstUpsellPreviewForThisPageLoad: FirstUpsellVersion | null | undefined;

function readFirstUpsellPreviewFromTheUrlOrTheTab(): FirstUpsellVersion | null {
  if (!import.meta.env.DEV) return null;
  try {
    const valueFromUrl = new URLSearchParams(window.location.search).get(FIRST_UPSELL_URL_PARAMETER);
    if (isFirstUpsellVersion(valueFromUrl)) {
      window.sessionStorage.setItem(FIRST_UPSELL_STORAGE_KEY, valueFromUrl);
      return valueFromUrl;
    }
    const valueFromTheTab = window.sessionStorage.getItem(FIRST_UPSELL_STORAGE_KEY);
    return isFirstUpsellVersion(valueFromTheTab) ? valueFromTheTab : null;
  } catch {
    // Storage can be blocked: no preview
    return null;
  }
}

/** Which upsell follows the first review: a development build's preview when one is chosen, else production's pop-up. */
export function readFirstUpsellVersion(): FirstUpsellVersion {
  if (firstUpsellPreviewForThisPageLoad === undefined) {
    firstUpsellPreviewForThisPageLoad = readFirstUpsellPreviewFromTheUrlOrTheTab();
  }
  return firstUpsellPreviewForThisPageLoad ?? getFirstUpsellVersionInProduction();
}

/** Tests only: forget what this page load read. */
export function forgetFirstUpsellVersionForTests(): void {
  firstUpsellPreviewForThisPageLoad = undefined;
}
